import React, { useEffect } from 'react';
import { Box, Grid, Text, Paper, Table, ScrollArea, Stack } from '@mantine/core';
import { useQuery } from '@apollo/client';
import { GET_SALESPERSON_PROFILE } from '../../../queries';

import AssignedAccountsGrid from './AssignedAccountsGrid';
import SalesPerformancePanel from './SalesPerformancePanel';
import SalesHistoryChart from './SalesHistoryChart'; // Import the chart component

const InfoItem = ({ label, value }) => (
    <Box>
        <Text size="sm" fw={500}>{label}:</Text>
        <Text size="sm">{value}</Text>
    </Box>
);

const Section = ({ title, children }) => (
    <Paper withBorder p="xs" mb="xs">
        <Text size="md" fw={600} mb="xs">{title}</Text>
        <Box>
            {children}
        </Box>
    </Paper>
);

const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    if (amount === 0) return '$0';
    const absAmount = Math.abs(Math.round(amount)).toLocaleString();
    return amount < 0 ? `-$${absAmount}` : `$${absAmount}`;
};

const formatPercent = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (value === 0) return '0%';
    const absValue = Math.abs(value).toFixed(2);
    return value < 0 ? `-${absValue}%` : `${absValue}%`;
};

const SalespersonProfile = ({ selectedSalesperson }) => {
    const { data, loading, error } = useQuery(GET_SALESPERSON_PROFILE, {
        variables: { ccode: selectedSalesperson?.cCode || '' },
        skip: !selectedSalesperson,
    });

    useEffect(() => {
        if (error) {
            console.error('Error fetching customer profile:', error);
        }
    }, [error]);

    if (loading) return <Text p="sm">Loading...</Text>;
    if (error) return <Text p="sm" c="red">Error: {error.message}</Text>;
    if (!data) return <Text p="sm">No data available</Text>;

    const profile = data.salespersonProfileByCcode;

    return (
        <Stack gap="xs" p="xs">
            {/* Scorecard Section */}
            <Section title="Scorecard">
                <SalesPerformancePanel ccode={selectedSalesperson?.cCode} />
            </Section>

            {/* Sales History Chart Section */}
            <Section title="Sales History">
                <SalesHistoryChart ccode={selectedSalesperson?.cCode} />
            </Section>

            {/* Production and Assigned Accounts Sections */}
            <Grid gutter="xs">
                <Grid.Col span={{ base: 12, md: 6 }}>
                    {/* Production Section */}
                    <Section title="Production">
                        <ScrollArea>
                            <Table size="sm" striped highlightOnHover withTableBorder>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th></Table.Th>
                                        <Table.Th>Quarter to Date</Table.Th>
                                        <Table.Th>6 months</Table.Th>
                                        <Table.Th>12 months</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    <Table.Tr>
                                        <Table.Td>
                                            <Text size="xs" fw={500}>Avg Quotes per day</Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size="xs">
                                                {formatCurrency(profile.avgQuotesPerDayDollarsQr || 0)} ({Math.round(profile.avgQuotesPerDayLinesQr || 0)} lines)
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size="xs">
                                                {formatCurrency(profile.avgQuotesPerDayDollars6m || 0)} ({Math.round(profile.avgQuotesPerDayLines6m || 0)} lines)
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size="xs">
                                                {formatCurrency(profile.avgQuotesPerDayDollars12m || 0)} ({Math.round(profile.avgQuotesPerDayLines12m || 0)} lines)
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                    <Table.Tr>
                                        <Table.Td>
                                            <Text size="xs" fw={500}>Avg Sales per day</Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size="xs">
                                                {formatCurrency(profile.avgSalesPerDayDollarsQr || 0)} ({Math.round(profile.avgSalesPerDayLinesQr || 0)} lines)
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size="xs">
                                                {formatCurrency(profile.avgSalesPerDayDollars6m || 0)} ({Math.round(profile.avgSalesPerDayLines6m || 0)} lines)
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size="xs">
                                                {formatCurrency(profile.avgSalesPerDayDollars12m || 0)} ({Math.round(profile.avgSalesPerDayLines12m || 0)} lines)
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                    <Table.Tr>
                                        <Table.Td>
                                            <Text size="xs" fw={500}>Avg RFQs per day</Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size="xs">
                                                {Math.round(profile.avgRfqsPerDayLinesQr || 0)} lines
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size="xs">
                                                {Math.round(profile.avgRfqsPerDayLines6m || 0)} lines
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size="xs">
                                                {Math.round(profile.avgRfqsPerDayLines12m || 0)} lines
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                </Table.Tbody>
                            </Table>
                        </ScrollArea>
                    </Section>
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 6 }}>
                    {/* Assigned Accounts Section */}
                    <Section title="Assigned Accounts">
                        <AssignedAccountsGrid ccode={selectedSalesperson.cCode} />
                    </Section>
                </Grid.Col>
            </Grid>
        </Stack>
    );
};

export default SalespersonProfile;